import { useQuery, useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Dropdown } from "../../Dropdown";
import { Icon } from "../../Icon";
import { Input } from "../../Input";
import { Modal } from "../../Modal";
import { Text } from "../../Text";
import { OidcProviderModal } from "../OidcProvidersSettings/OidcProviderModal";
import {
  DropdownOpener,
  FieldLabel,
  ModalContent,
  ModalFooter,
} from "../shared";

export function AddFederationModal({
  serviceAccountId,
  onClose,
  onAdd,
}: {
  serviceAccountId: string;
  onClose: () => void;
  // When provided (service account create flow), buffer the federation instead
  // of creating it — the service account doesn't exist yet.
  onAdd?: (f: {
    providerId: string;
    providerName: string;
    subject: string;
    name: string;
  }) => void;
}) {
  const z = useZero();
  const [providers] = useQuery(queries.oidcProviders());
  const canManageOidc = useHasAccess("tenant:oidc_providers_manage");
  const [providerId, setProviderId] = useState("");
  const [subject, setSubject] = useState("");
  const [name, setName] = useState("");
  const [showRegister, setShowRegister] = useState(false);

  const selectedProvider = providers.find((p) => p.id === providerId);

  const add = () => {
    if (!providerId || !subject.trim()) return;
    const finalName = name.trim() || selectedProvider?.name || "federation";
    if (onAdd) {
      onAdd({
        providerId,
        providerName: selectedProvider?.name ?? finalName,
        subject: subject.trim(),
        name: finalName,
      });
    } else {
      z.mutate(
        sharedMutators.createServiceAccountFederation({
          id: nanoid(12),
          serviceAccountId,
          providerId,
          name: finalName,
          subject: subject.trim(),
        })
      );
    }
    onClose();
  };

  // Register a provider inline and select it, so first-time setup doesn't
  // dead-end on an empty provider list.
  const registerProvider = (values: {
    name: string;
    issuer: string;
    audience: string;
    jwksUri?: string;
  }) => {
    const id = nanoid(12);
    z.mutate(sharedMutators.registerOidcProvider({ id, ...values }));
    setProviderId(id);
  };

  return (
    <Modal open onClose={onClose} alert>
      <ModalContent>
        <Text variant="text-regular-md">Add federation</Text>
        {providers.length === 0 ? (
          <>
            <Box horizontal={8} top={6}>
              <Text variant="text-small" color="var(--text-secondary)">
                {canManageOidc
                  ? "No identity providers yet. Add one so a workload can act as this service account."
                  : "No identity providers yet. An admin needs to add one before a workload can act as this service account."}
              </Text>
            </Box>
            <div style={{ height: 8 }} />
            <ModalFooter>
              {canManageOidc ? (
                <>
                  <Button size="l" variant="subtle" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button size="l" onClick={() => setShowRegister(true)}>
                    Add identity provider
                  </Button>
                </>
              ) : (
                <Button size="l" onClick={onClose}>
                  Done
                </Button>
              )}
            </ModalFooter>
          </>
        ) : (
          <>
            <FieldLabel>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Identity provider</Text>
              </Box>
              <Dropdown
                align="bottom-left"
                opener={
                  <DropdownOpener>
                    <Text variant="text-regular" ellipsis>
                      {selectedProvider?.name ?? "Select a provider…"}
                    </Text>
                    <span style={{ opacity: 0.3 }}>
                      <Icon name="chevron-down" size={12} />
                    </span>
                  </DropdownOpener>
                }
                sections={[
                  {
                    items: providers.map((p) => ({
                      type: "button" as const,
                      left: <Icon name="id-card-lanyard" size={16} />,
                      label: p.name,
                      right:
                        p.id === providerId ? (
                          <Icon
                            name="check"
                            size={12}
                            color="var(--text-secondary)"
                          />
                        ) : undefined,
                      onClick: () => setProviderId(p.id),
                    })),
                  },
                  ...(canManageOidc
                    ? [
                        {
                          items: [
                            {
                              type: "button" as const,
                              left: <Icon name="plus" size={16} />,
                              label: "Add identity provider",
                              onClick: () => setShowRegister(true),
                            },
                          ],
                        },
                      ]
                    : []),
                ]}
              />
            </FieldLabel>

            <FieldLabel>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Subject</Text>
              </Box>
              <Input
                placeholder="e.g. repo:org/repo:*"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                style={{ minWidth: 0, width: "100%" }}
              />
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  The token’s `sub` claim. Match it exactly, or use a prefix
                  like repo:org/repo:* to allow any branch.
                </Text>
              </Box>
            </FieldLabel>

            <FieldLabel>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Name</Text>
              </Box>
              <Input
                placeholder="Optional"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ minWidth: 0, width: "100%" }}
              />
            </FieldLabel>

            <div style={{ height: 8 }} />
            <ModalFooter>
              <Button size="l" variant="subtle" onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="l"
                onClick={add}
                disabled={!providerId || !subject.trim()}
              >
                Add federation
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
      {showRegister && (
        <OidcProviderModal
          alert
          onClose={() => setShowRegister(false)}
          onSubmit={registerProvider}
        />
      )}
    </Modal>
  );
}
